import { useEffect, useMemo, useState, useRef } from "react";

export default function SectionVisibilityPanel({
    open,
    onClose,
    sections,
    storageKey,
    defaults = {},
    floating = false,
}) {
    const frameRef = useRef(null);

    const initial = useMemo(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw) return JSON.parse(raw);
        } catch {}
        return Object.fromEntries(
            sections.map((s) => [s.key, defaults[s.key] ?? true])
        );
    }, [sections, storageKey, defaults]);

    const [vis, setVis] = useState(initial);

    const hasChanges = useMemo(
        () => JSON.stringify(vis) !== JSON.stringify(initial),
        [vis, initial]
    );

    const handleValidate = () => {
        localStorage.setItem(storageKey, JSON.stringify(vis));
        onClose?.();
    };

    // Fermer : clic extérieur + ESC (clic extérieur = sauvegarde aussi)
    useEffect(() => {
        if (!open) return;

        const onDown = (e) => {
            if (frameRef.current && !frameRef.current.contains(e.target)) {
                // ⬅️ On sauvegarde puis on ferme
                handleValidate();
            }
        };

        const onKey = (e) => {
            if (e.key === "Escape") {
                // ici tu peux choisir : valider ou juste fermer
                // pour l’instant on ferme sans sauvegarder
                onClose?.();
            }
        };

        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, handleValidate, onClose]);
    // ...

    const allOn = sections.every((s) => !!vis[s.key]);
    const allOff = sections.every((s) => !vis[s.key]);
    const setAll = (v) =>
        setVis(Object.fromEntries(sections.map((s) => [s.key, v])));
    const reset = () =>
        setVis(
            Object.fromEntries(
                sections.map((s) => [s.key, defaults[s.key] ?? true])
            )
        );

    const Toggle = ({ checked, onChange, disabled }) => (
        <label className="inline-flex items-center cursor-pointer select-none">
            <input
                type="checkbox"
                className="sr-only peer"
                checked={checked}
                onChange={(e) => onChange?.(e.target.checked)}
                disabled={disabled}
                aria-checked={checked}
                role="switch"
            />
            <span
                className={[
                    "relative w-14 h-8 rounded-full transition-colors duration-300 ease-in-out",
                    "bg-gray-300 peer-checked:bg-orange-500",
                    disabled ? "opacity-50 cursor-not-allowed" : "",
                ].join(" ")}
            >
                <span
                    className={[
                        "absolute left-1 top-1 w-6 h-6 bg-white rounded-full shadow-md",
                        "transition-transform duration-300 ease-in-out will-change-transform",
                        "peer-checked:translate-x-6 translate-x-0",
                    ].join(" ")}
                />
            </span>
        </label>
    );

    const PanelInner = (
        <>
            {/* En-tête */}
            <div className="flex justify-between items-center bg-gradient-to-r from-orange-50 to-white px-4 py-3 border-b rounded-t-xl">
                <div className="flex items-center gap-2">
                    <span className="place-items-center grid bg-orange-100 rounded-lg w-8 h-8 text-orange-600">
                        <i className="fa-regular fa-eye" />
                    </span>
                    <div>
                        <h4 className="font-semibold text-gray-800 leading-tight">
                            Sections affichées
                        </h4>
                        <p className="text-gray-500 text-xs leading-none">
                            Coche/décoche pour afficher ou masquer les blocs
                        </p>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="hover:bg-gray-100 p-2 rounded-md text-gray-500 transition"
                    aria-label="Fermer"
                >
                    <i className="fa-solid fa-xmark" />
                </button>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3 px-4 py-3 border-b">
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        className="inline-flex items-center gap-2 hover:bg-gray-50 disabled:opacity-50 px-3 py-2 border rounded-md text-sm transition"
                        onClick={() => setAll(true)}
                        disabled={allOn}
                        title="Afficher toutes les sections"
                    >
                        <i className="text-green-600 fa-solid fa-toggle-on" />
                        Tout cocher
                    </button>
                    <button
                        className="inline-flex items-center gap-2 hover:bg-gray-50 disabled:opacity-50 px-3 py-2 border rounded-md text-sm transition"
                        onClick={() => setAll(false)}
                        disabled={allOff}
                        title="Masquer toutes les sections"
                    >
                        <i className="text-gray-500 fa-solid fa-toggle-off" />
                        Tout décocher
                    </button>
                    <button
                        className="inline-flex items-center gap-2 hover:bg-gray-50 px-3 py-2 border rounded-md text-sm transition bg-orange-600 hover:bg-orange-700 text-white"
                        onClick={reset}
                        title="Réinitialiser aux valeurs par défaut"
                    >
                        <i className="fa-rotate-left fa-solid" />
                        Réinitialiser
                    </button>
                </div>

                <div className="flex justify-center">
                    <button
                        className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded-md text-white text-sm font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
                        onClick={handleValidate}
                        disabled={!hasChanges}
                        title="Valider les changements"
                    >
                        <i className="fa-solid fa-check" />
                        Valider
                    </button>
                </div>
            </div>

            {/* Liste */}
            <ul className="p-2">
                {sections.map((s, idx) => (
                    <li
                        key={s.key}
                        className="opacity-0 translate-y-1 animate-[fadein_.25s_ease-out_forwards]"
                        style={{
                            animationDelay: `${Math.min(idx * 40, 280)}ms`,
                        }}
                    >
                        <label className="group flex justify-between items-center gap-3 hover:bg-orange-50 px-3 py-2 rounded-lg transition">
                            <div className="flex items-center gap-3 min-w-0">
                                <span className="place-items-center grid bg-gray-100 group-hover:bg-orange-100 rounded-md w-7 h-7 text-gray-500 group-hover:text-orange-600 transition">
                                    <i className="fa-layer-group text-xs fa-solid" />
                                </span>
                                <span className="text-gray-800 text-sm truncate">
                                    {s.label}
                                </span>
                            </div>
                            <Toggle
                                checked={!!vis[s.key]}
                                onChange={(v) =>
                                    setVis((prev) => ({ ...prev, [s.key]: v }))
                                }
                            />
                        </label>
                    </li>
                ))}
            </ul>
        </>
    );

    if (!open) return null;

    return (
        <>
            <div className="relative">
                <div
                    ref={frameRef}
                    className={[
                        "z-[60] absolute max-w-full right-0 top-[calc(100%-8px)]",
                        "bg-white border border-orange-100 shadow-2xl rounded-xl",
                        "w-[min(28rem,calc(100vw-1.5rem))]",
                        "animate-[panelIn_.18s_ease-out]",
                        "overflow-visible",
                    ].join(" ")}
                    role="dialog"
                    aria-label="Affichage des sections"
                >
                    <div className="-top-2 right-[5.3rem] absolute bg-white border-orange-100 border-t border-l w-4 h-4 rotate-45 pointer-events-none" />
                    <div className="rounded-xl max-h-[70vh] overflow-auto">
                        {PanelInner}
                    </div>
                </div>
            </div>

            <style>{`
        @keyframes panelIn {
          0%   { opacity: 0; transform: translateY(6px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0)   scale(1);    }
        }
        @keyframes fadein {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
        </>
    );
}
